import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Button from "components/ui/Button";
import Loader from "components/ui/Loader";
import { ProductApiConfig } from "api/configs/productApiConfig";
import { useApiMutate } from "hooks/useApi";

const AI_MODELS = [
  { value: "freeLLM", label: "FreeLLM", modelType: "freeLLM" },
  { value: "gemini-2.5-flash-lite", label: "gemini-2.5-flash-lite", modelType: "gemini" },
  { value: "gemini-1.5-flash-latest", label: "gemini-1.5-flash-latest", modelType: "gemini" },
  { value: "gemini-1.5-pro", label: "gemini-1.5-pro", modelType: "gemini" },
] as const;

type ModelValue = (typeof AI_MODELS)[number]["value"];

type AIVerificationProps = {
  productData: any;
  productSpecs: any[];
  attributesMetaData: any[];
};

function buildProductSummary(
  productData: any,
  productSpecs: any[],
  attributesMetaData: any[]
): string {
  const lines: string[] = [];
  lines.push("=== PRODUCT DATA FROM API ===");
  lines.push(`Model Name: ${productData.modelName || "N/A"}`);
  lines.push(`ASN: ${productData.asn || "N/A"}`);
  lines.push(`Display Name: ${productData.displayName || "N/A"}`);
  lines.push(`Brand: ${productData.brand?.name || productData.brand || "N/A"}`);
  lines.push(`MRP: ${productData.mrp ?? "N/A"}`);
  lines.push(`Category: ${productData.categories?.[0]?.name || "N/A"}`);
  lines.push(`Description: ${productData.description || "N/A"}`);
  lines.push(`Highlights: ${productData.highlights || "N/A"}`);
  lines.push(`Generic Name: ${productData.genericName || "N/A"}`);
  lines.push(`Country of Origin: ${productData.countryOfOrigin || "N/A"}`);
  lines.push(`Manufacturer Details: ${productData.manufacturerDetails || "N/A"}`);
  lines.push(`Sales Package: ${productData.salesPackage || "N/A"}`);

  lines.push("\n=== PRODUCT SPECIFICATIONS ===");
  const specNames: string[] = [];
  if (attributesMetaData?.length && productSpecs?.length) {
    attributesMetaData.forEach((meta) => {
      const spec = productSpecs.find((s) => s?.k === meta._id);
      if (spec?.v) {
        const unit = spec.u ? ` ${spec.u}` : "";
        lines.push(`${meta.name}: ${spec.v}${unit}`);
        specNames.push(meta.name);
      }
    });
  }

  // Extract variant information for identification
  const variantInfo: string[] = [];
  if (attributesMetaData?.length && productSpecs?.length) {
    const variantSpecs = [
      "Water Tank Capacity", "Capacity", "Tank Capacity", "Reservoir Capacity",
      "Style Name", "Style", "Variant", "Model Variant", "Size", "Product Size",
      "Color", "Colour", "Model Number", "SKU", "Product Code"
    ];
    attributesMetaData.forEach((meta) => {
      const metaNameLower = meta.name.toLowerCase();
      if (variantSpecs.some(v => metaNameLower.includes(v.toLowerCase()))) {
        const spec = productSpecs.find((s) => s?.k === meta._id);
        if (spec?.v) {
          const unit = spec.u ? ` ${spec.u}` : "";
          variantInfo.push(`${meta.name}: ${spec.v}${unit}`);
        }
      }
    });
  }

  const searchTexts = [
    productData.displayName,
    productData.modelName,
    productData.description
  ].filter(Boolean).join(" ").toLowerCase();

  const capacityMatch = searchTexts.match(/(\d+)\s*l/i);
  if (capacityMatch) variantInfo.push(`Variant Capacity: ${capacityMatch[1]} L`);

  const styleMatch = searchTexts.match(/(koolbliss(?:\s+neo)?|style[:\s]+([a-z0-9\s]+))/i);
  if (styleMatch) variantInfo.push(`Variant Style: ${styleMatch[0].replace(/style[:\s]+/i, "").trim()}`);

  if (variantInfo.length > 0) {
    lines.push("\n=== VARIANT IDENTIFIERS (Use these to find exact variant on reference page) ===");
    variantInfo.forEach(v => lines.push(v));
  }

  // Build mandatory checklist — everything except ASN and Description
  const coreVerifyFields = [
    { field: "Model Name", value: productData.modelName },
    { field: "Brand", value: productData.brand?.name || productData.brand },
    { field: "Category", value: productData.categories?.[0]?.name },
    { field: "Generic Name", value: productData.genericName },
    { field: "Country of Origin", value: productData.countryOfOrigin },
    { field: "Manufacturer Details", value: productData.manufacturerDetails },
    { field: "Sales Package", value: productData.salesPackage },
  ];
  const coreFieldNames = coreVerifyFields
    .filter(({ value }) => value != null && value !== "")
    .map(({ field }) => field);

  lines.push("\n=== MANDATORY VERIFICATION CHECKLIST ===");
  lines.push("Every item below MUST appear as its own row in the verification table. Do not skip, merge, or omit any:");
  [...coreFieldNames, ...specNames].forEach(name => lines.push(`- ${name}`));
  lines.push("(Excluded from table: ASN, Description, Display Name, Highlights, MRP)");

  return lines.join("\n");
}

function buildLinkVerificationPrompt(productSummary: string, links: string[]): string {
  return `You are an automated product specification verifier. No user is present. Output the complete verification report in ONE response without asking any questions.

=== YOUR TASK ===
Read each reference URL below, extract the specifications for the exact product model described in the product data, and compare them against our database values.

=== REFERENCE URLs ===
${links.map((url, i) => `${i + 1}. ${url}`).join("\n")}

${productSummary}

=== ABSOLUTE RULES — READ CAREFULLY ===
1. ONLY use information found on the provided URLs above. Do NOT fill gaps using your training knowledge or memory.
2. Do NOT generate, suggest, or mention any URLs other than the ones listed above.
3. Do NOT fabricate reference values. If a spec is not visible on the page, mark it MISSING.
4. If a URL fails to load for any reason: mark every spec from that source as LINK FAILED. Do not substitute from memory or training data.
5. Never mix up variants — first identify the exact variant (capacity, color, model suffix) using the VARIANT IDENTIFIERS in the product data, then extract specs only for that matched variant.
6. Standby power ≠ Power Consumption. Cooling Power ≠ Power Consumption. Never substitute one for the other.
7. MANDATORY COMPLETENESS: Every item listed in the "MANDATORY VERIFICATION CHECKLIST" section of the product data MUST appear as its own row in the table. No exceptions — do not skip, group, or omit any checklist item even if the value seems unverifiable.

=== VARIANT IDENTIFICATION (do this first) ===
Before extracting any specs, check the "VARIANT IDENTIFIERS" section of the product data above.
Find the matching variant on the reference page (e.g., select the right capacity button, color option, or model tab).
Extract specs ONLY from that matched variant.

=== SEARCHING EACH PAGE ===
- Expand all collapsible sections and tabs: Specifications, Technical Details, Features, About This Item
- Look for values in all unit formats: "280 W", "0.28 kW", "280 Watts"
- Try label aliases before marking MISSING:

| Our Label | Also appears as |
|---|---|
| Weight | Item Weight, Net Weight, Product Weight |
| Water Tank Capacity | Tank Capacity, Reservoir Capacity, Water Capacity |
| Power Consumption | Rated Power, Operating Power, Running Power (NOT Standby, NOT Cooling Power) |
| Cooling Capacity | Cooling Power, BTU, Ton, Tonnage (1 Ton ≈ 3517 W) |
| Item Dimensions | Dimensions, L×W×H, Product Dimensions |
| Color | Colour, Color Family |
| Body Material | Material, Material Type, Construction |

=== UNIT CONVERSIONS (apply before marking MISMATCH) ===
- Spacing between number and unit is irrelevant: "708mm" = "708 mm", "5L" = "5 L", "280W" = "280 W" → always MATCH
- Case of unit is irrelevant: "708MM" = "708mm" → always MATCH
- g↔kg | ml↔L | mm↔cm↔m | W↔kW (1 kW = 1000 W) | lbs↔kg (1 kg = 2.205 lbs)
- Whitespace normalization: Before any comparison, normalize all whitespace. Trim leading/trailing spaces, convert all whitespace sequences (tabs, multiple spaces, line breaks) to single spaces. "  175L  Water Tank  " = "175L Water Tank" → always MATCH

=== SMART MATCHING RULES (apply before marking MISMATCH) ===
- Category field contains SUB-CATEGORY, not main category: Our "Category" field stores the sub-category (e.g., "single-door", "split-ac", "multiple-door", "window-ac"), not the main product category. When comparing against the reference page: (a) If reference shows a main category (e.g., "Refrigerator", "Air Conditioner"), verify it is compatible with our sub-category (e.g., "single-door" is a type of refrigerator → MATCH); (b) If reference shows the specific sub-category type in the product description (e.g., "Single Door Refrigerator" contains "single-door") → MATCH; (c) If reference explicitly lists the sub-category → MATCH.
- Split AC weight: For split air conditioners (category "split-ac"), the total weight is the sum of indoor net weight and outdoor net weight. If the reference page shows separate indoor and outdoor weights, add them to calculate the total weight before comparing.
- Numeric range vs single value: If our database value is a numeric range (e.g., "50-60Hz", "100-240V") and the reference shows a single value that falls within that range (e.g., "60Hz"), mark it MATCH. A single endpoint that satisfies the range confirms it.
- Size-based category: If our category name encodes a display-size range (e.g., "Above 32", "Below 32", "32-43 inch"), do NOT compare it literally against the reference page's category label. Instead, check the product's own display size spec. If the display size satisfies the category range (e.g., 43 inches satisfies "Above 32"), mark the category as MATCH.
- Year embedded in model number: If the model number contains a year as a suffix (e.g., "/2026" or "-2026"), treat that as the authoritative product year. If the reference page shows a different year but the model number suffix matches our database year, mark the year field as MATCH and note that the model number year takes precedence over the reference page.
- Text substring/superset matching: If one text value is a substring of the other or both contain the same key phrases/numbers (ignoring minor differences like punctuation, abbreviations, or extra words), mark it MATCH. Examples:
  - "175L Water Tank, 30-Inch Heavy-Duty, 550W Motor" vs "175L Water Tank, 30-Inch Heavy-Duty Fan, 550W Motor" → all key specs match, MATCH
  - "Eecocool Home Appliances" vs "Eecocool Home Appliances Pvt. Ltd." → same company name with legal suffix, MATCH
  - "Eecocool Home Appliances Pvt. Ltd., Address details..." vs "Eecocool Home Appliances Pvt. Ltd." → same company, database has full address, MATCH
- Legal entity name variations: Company names with "Pvt. Ltd.", "Ltd.", "Inc.", "Corp.", or similar suffixes should match the base company name. "ABC Company" MATCH "ABC Company Pvt. Ltd."

=== OUTPUT FORMAT ===

**Link Status:**
${links.map((url, i) => `- Link ${i + 1}: ${url} → [Loaded / FAILED: reason]`).join("\n")}

In the "Reference Value (Source)" column, always append the link number and full URL in brackets after the value, e.g.:
- "280 W [Link 1: https://...]"
- If found on multiple links: "280 W [Link 1: https://...] [Link 2: https://...]"
- If the link failed or spec was not found, write "— [Link 1: FAILED]" or "— [not found on Link 1: https://...]"

| # | Specification | Our Database Value | Reference Value (Source) | Status |
|---:|---|---|---|---|
| 1 | Category | ... | value [Link N: url] | MATCH / MISMATCH / MISSING / LINK FAILED |
| 2 | Model Name | ... | value [Link N: url] | ... |
| 3 | Brand | ... | value [Link N: url] | ... |
| 4+ | [every other spec from product data, in order] | ... | value [Link N: url] | ... |

Status legend: MATCH · MISMATCH · MISSING (not found on page) · LINK FAILED (URL did not load)

Notes on rows 1–3:
- Category: check page title and breadcrumb trail first
- Model Name: case-insensitive, ignore spaces and hyphens ("AIR-FORCE100" = "Air Force 100")
- Brand: check title, brand section, or manufacturer field

**Summary:** N specs checked — A matched, B mismatched, C missing, D link failed

Output the table now. No introduction or explanation before the table.`;
}

function buildKnowledgeVerificationPrompt(
  productSummary: string,
  modelName: string,
  brand: string
): string {
  return `You are an automated product specification verifier. No user is present. Output the complete verification report in ONE response without asking any questions.

=== YOUR TASK ===
No reference URLs were provided. Search the web and use your knowledge to find the correct, authoritative specifications for the exact product model below. Show real values with real sources.

${productSummary}

=== SEARCH TARGET ===
Model: "${modelName}"
Brand: "${brand}"

=== ABSOLUTE RULES — READ CAREFULLY ===
1. Search for the EXACT model "${modelName}" by BRAND "${brand}" ONLY. Do not use a similar, related, different-year, or different-brand model. If you cannot find this exact model by this exact brand, mark ALL specs as CANNOT VERIFY and set Confidence to Low.
2. BRAND VALIDATION FIRST: Before extracting any specifications, verify that the model you found is manufactured by "${brand}". If the model is made by a DIFFERENT brand, STOP and output the table with BRAND as MISMATCH and all other specs as CANNOT VERIFY. Do not continue searching for similar models from other brands.
3. Find the correct value for each spec from a reliable source: official brand website, manufacturer product page, or major retailer (Amazon, Flipkart, Croma, etc.).
4. In the "Correct Value (Source)" column, show the actual correct value followed by the source — either a real URL you found (e.g., "280 W [symphony.in/product-page]") or a source name if a full URL is not available (e.g., "280 W [Symphony official site]"). Only include a URL if you are certain it leads to this exact model.
5. Do NOT fabricate values. If you cannot find reliable data for a specific spec of this exact model and variant, mark it CANNOT VERIFY.
6. Do NOT include a URL unless you are confident it is a current, live page for this model — a wrong URL is worse than no URL.
7. If the model has variants (different capacities, colors, sizes), state which variant your data covers and match it to the VARIANT IDENTIFIERS in the product data above.
8. Standby power ≠ Power Consumption. Cooling Power ≠ Power Consumption. Never substitute.
9. MANDATORY COMPLETENESS: Every item listed in the "MANDATORY VERIFICATION CHECKLIST" section of the product data MUST appear as its own row in the table. No exceptions — do not skip, group, or omit any checklist item even if you cannot verify it (use CANNOT VERIFY in that case).
10. MODEL & BRAND MATCHING: Row 1 must verify Category, Row 2 must verify Model Name (EXACT match, case-insensitive), Row 3 must verify Brand (MUST match "${brand}" exactly). If Brand in row 3 is anything other than "${brand}", mark as MISMATCH and add a note: "[ALERT: Found different brand - this may be wrong product]".

=== TEXT NORMALIZATION (apply before any comparison) ===
- Whitespace normalization: Trim leading/trailing spaces, convert all whitespace sequences (tabs, multiple spaces, line breaks) to single spaces. "  175L  Water Tank  " = "175L Water Tank" → always MATCH

=== SMART MATCHING RULES (apply before marking MISMATCH) ===
- Category field contains SUB-CATEGORY, not main category: Our "Category" field stores the sub-category (e.g., "single-door", "split-ac", "multiple-door", "window-ac"), not the main product category. When comparing: (a) If reference shows main category (e.g., "Refrigerator"), verify it is compatible with our sub-category (e.g., "single-door" is a type of refrigerator) → MATCH; (b) If reference shows sub-category in product description (e.g., "Single Door Refrigerator") → MATCH.
- Split AC weight: For split air conditioners (category "split-ac"), the total weight is the sum of indoor net weight and outdoor net weight. If the source shows separate indoor and outdoor weights, add them to calculate the total weight before comparing.
- Numeric range vs single value: If our database value is a numeric range (e.g., "50-60Hz", "100-240V") and the reference shows a single value that falls within that range (e.g., "60Hz"), mark it MATCH.
- Size-based category: If our category name encodes a display-size range (e.g., "Above 32", "Below 32"), check the product's display size spec instead of comparing category labels literally. If the display size satisfies the range, mark category as MATCH.
- Year embedded in model number: If the model number contains a year suffix (e.g., "/2026"), treat that as the authoritative product year. If a source shows a different year but the model suffix matches our year, mark the year field as MATCH.
- Text substring/superset matching: If one text value is a substring of the other or both contain the same key phrases/numbers, mark it MATCH. Examples: "Eecocool Home Appliances" vs "Eecocool Home Appliances Pvt. Ltd." → MATCH; "175L Water Tank, 30-Inch, 550W Motor" vs "175L Water Tank, 30-Inch Fan, 550W Motor" → MATCH.
- Legal entity name variations: "ABC Company" MATCH "ABC Company Pvt. Ltd." or "ABC Company Ltd."

=== STATUS LEVELS ===
- MATCH: Correct value confirms the database value matches
- MISMATCH: Correct value is clearly different from the database value
- CANNOT VERIFY: No reliable data found for this spec / variant — do not guess
- UNCERTAIN: Partial or conflicting data found across sources

=== OUTPUT FORMAT ===

**Source:** [primary source used, e.g., "Symphony official website" or "Amazon.in product listing"]
**Model verified:** ${modelName} by ${brand}
**Variant matched:** [which variant your data covers, e.g., "65L KOOLBLISS Neo — white"]
**Confidence:** High / Medium / Low — [one-line reason. NOTE: If Brand row shows MISMATCH or is not "${brand}", this MUST be Low confidence with explanation that brand mismatch was found]

| # | Specification | Our Database Value | Correct Value (Source) | Status |
|---:|---|---|---|---|
| 1 | Model Name | ... | value [source] | MATCH / MISMATCH / CANNOT VERIFY / UNCERTAIN |
| 2 | Brand | ... | value [source] | ... |
| 3 | Category | ... | value [source] | ... |
| 4+ | [every remaining item from MANDATORY VERIFICATION CHECKLIST, in order] | ... | value [source] | ... |

**Summary:** N specs checked — A matched, B mismatched, C cannot verify, D uncertain

CRITICAL FINAL CHECK: Before outputting the table, verify that:
1. Row 3 (Brand) shows "${brand}" in the "Correct Value" column — if it shows a different brand, this MUST be marked as MISMATCH and Confidence must be set to Low with a clear warning about brand mismatch.
2. Row 2 (Model Name) matches the provided model "${modelName}" (case-insensitive, spaces/hyphens ignored).
3. If BOTH Brand and Model Name match with high confidence, then set Confidence to High. Otherwise, set it to Medium or Low.

Output the table now. No introduction or explanation before the table.`;
}

function buildPrompt(
  productSummary: string,
  links: string[],
  searchByModelName: boolean = false,
  modelName: string = "",
  brand: string = ""
): string {
  const validLinks = links.filter(l => l.trim());
  if (validLinks.length > 0) {
    return buildLinkVerificationPrompt(productSummary, validLinks);
  }
  if (searchByModelName && modelName) {
    return buildKnowledgeVerificationPrompt(productSummary, modelName, brand);
  }
  return "No reference links or search criteria provided.";
}

function validateBrandInResponse(
  response: string,
  expectedBrand: string,
  expectedModel: string
): { isValid: boolean; warning: string } {
  const lines = response.split("\n");
  const brandRowMatch = lines.find(line => /^\|\s*[23]\s*\|\s*Brand\s*\|/i.test(line));
  
  if (!brandRowMatch) {
    return { isValid: false, warning: "⚠️ [VALIDATION ALERT] Brand row not found in verification table. This may indicate incorrect model was used." };
  }

  // Extract the "Correct Value" from the brand row (3rd column)
  const cols = brandRowMatch.split("|").map(c => c.trim());
  const correctValue = cols[3]?.toLowerCase() || "";
  const expectedBrandLower = expectedBrand.toLowerCase();

  // Check if the brand in the response matches the expected brand
  if (!correctValue.includes(expectedBrandLower) && !expectedBrandLower.includes(correctValue.split("[")[0].trim())) {
    return {
      isValid: false,
      warning: `⚠️ [CRITICAL ALERT] Brand mismatch detected!\n` +
        `Expected: "${expectedBrand}"\n` +
        `Found: "${correctValue.split("[")[0].trim()}"\n` +
        `This indicates the wrong product model or variant was found. Please verify the model name "${expectedModel}" is correct, or consider using reference links (Flipkart, Amazon) for more accurate verification.`
    };
  }

  return { isValid: true, warning: "" };
}

export default function AIVerification({
  productData,
  productSpecs,
  attributesMetaData,
}: AIVerificationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [links, setLinks] = useState(["", ""]);
  const [selectedModel, setSelectedModel] = useState<ModelValue>("gemini-2.5-flash-lite");
  const [verificationMode, setVerificationMode] = useState<"links" | "ai-search">("links");
  const [statusMsg, setStatusMsg] = useState("");
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [validationWarning, setValidationWarning] = useState("");
  const [expectedBrand, setExpectedBrand] = useState("");
  const [expectedModel, setExpectedModel] = useState("");

  const parseAIResponse = (data: any): string => {
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return parsed?.message || parsed?.response || data;
      } catch {
        return data;
      }
    }
    return (
      data?.message ||
      data?.response ||
      data?.result ||
      data?.choices?.[0]?.message?.content ||
      JSON.stringify(data, null, 2)
    );
  };

  const { mutate: verifyProduct, isLoading: verifying } = useApiMutate(
    ProductApiConfig.AI_VERIFY_PRODUCT,
    {
      successCb: (data: any) => {
        const responseText = parseAIResponse(data);
        setResult(responseText);
        setStatusMsg("");

        // Validate brand if using AI search mode
        if (verificationMode === "ai-search" && expectedBrand && expectedModel) {
          const { isValid, warning } = validateBrandInResponse(responseText, expectedBrand, expectedModel);
          if (!isValid) {
            setValidationWarning(warning);
          } else {
            setValidationWarning("");
          }
        }
      },
      errorCb: (err: any) => {
        setError(
          typeof err === "string"
            ? err
            : err?.message || "Failed to get AI verification response"
        );
        setStatusMsg("");
        setValidationWarning("");
      },
    }
  );

  const hasValidLinks = links.some((l) => l.trim());
  const hasProduct = productData?.modelName;
  const canVerify = hasProduct && (verificationMode === "ai-search" || hasValidLinks);

  const handleVerify = () => {
    if (!canVerify) return;

    setResult("");
    setError("");
    setValidationWarning("");

    const validLinks = links.filter((l) => l.trim());
    const useLinks = verificationMode === "links";

    setStatusMsg(
      useLinks
        ? `Attempting to fetch and verify ${validLinks.length} reference link(s). AI will try hard to load all links...`
        : `Searching for EXACT product model "${productData.modelName}" by brand "${productData.brand?.name || productData.brand}" from reliable sources. Ensuring brand match...`
    );

    const productSummary = buildProductSummary(productData, productSpecs, attributesMetaData);
    const prompt = buildPrompt(
      productSummary,
      useLinks ? validLinks : [],
      !useLinks,
      productData.modelName || "",
      productData.brand?.name || productData.brand || ""
    );

    // Store expected values for brand validation
    if (!useLinks) {
      setExpectedBrand(productData.brand?.name || productData.brand || "");
      setExpectedModel(productData.modelName || "");
    }

    const selected = AI_MODELS.find((m) => m.value === selectedModel)!;
    verifyProduct({
      message: prompt,
      modelType: selected.modelType,
      model: selected.value,
    });
  };

  const handleLinkChange = (index: number, value: string) => {
    setLinks((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleClose = () => setIsOpen(false);

  const handleReset = () => {
    setLinks(["", ""]);
    setSelectedModel("gemini-2.5-flash-lite");
    setVerificationMode("links");
    setResult("");
    setError("");
    setStatusMsg("");
    setValidationWarning("");
    setExpectedBrand("");
    setExpectedModel("");
  };

  return (
    <>
      <Button
        text="Verify with AI"
        onButtonClick={() => setIsOpen(true)}
        btnClass="bg-btn px-[20px] py-[10px]"
        disabled={!hasProduct}
      />

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-start justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-2xl transition-all my-8">
                  {/* Header */}
                  <div className="bg-btn px-6 py-4 flex items-center justify-between">
                    <Dialog.Title className="text-lg font-bold text-white">
                      AI Product Verification
                    </Dialog.Title>
                    <button
                      onClick={handleClose}
                      className="text-white hover:text-gray-200 text-2xl font-bold leading-none"
                    >
                      &times;
                    </button>
                  </div>

                  {/* Product Summary */}
                  <div className="px-6 py-4 bg-gray-50 border-b my-6">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="font-bold text-gray-600">Model: </span>
                        <span className="text-gray-900">{productData.modelName || "N/A"}</span>
                      </div>
                      <div>
                        <span className="font-bold text-gray-600">ASN: </span>
                        <span className="text-gray-900">{productData.asn || "N/A"}</span>
                      </div>
                      <div>
                        <span className="font-bold text-gray-600">Brand: </span>
                        <span className="text-gray-900">
                          {productData.brand?.name || productData.brand || "N/A"}
                        </span>
                      </div>
                      <div>
                        <span className="font-bold text-gray-600">Category: </span>
                        <span className="text-gray-900">
                          {productData.categories?.[0]?.name || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Verification Mode Selection */}
                  <div className="px-6 py-4 border-b">
                    <h3 className="font-bold text-gray-700 mb-3">Verification Method</h3>
                    <div className="flex gap-2 border-b border-gray-200">
                      <button
                        type="button"
                        onClick={() => setVerificationMode("links")}
                        disabled={verifying}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          verificationMode === "links"
                            ? "text-btn border-b-2 border-btn bg-gray-50"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        Verify with Reference Links
                      </button>
                      <button
                        type="button"
                        onClick={() => setVerificationMode("ai-search")}
                        disabled={verifying}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          verificationMode === "ai-search"
                            ? "text-btn border-b-2 border-btn bg-gray-50"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        AI Search by Model Name
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {verificationMode === "links"
                        ? "Provide reference URLs for AI to fetch and verify"
                        : "AI will explore the internet for product information using model name and brand"}
                    </p>
                  </div>

                  {/* Links Input */}
                  {verificationMode === "links" && (
                    <div className="px-6 py-4 border-b my-6">
                      <h3 className="font-bold text-gray-700 mb-3">
                        Reference Links (AI will fetch these)
                      </h3>
                      <div className="space-y-3">
                        {links.map((link, idx) => (
                          <div key={idx} className="grid grid-cols-[60px_1fr_auto] items-center gap-4">
                            <label className="text-sm font-medium text-gray-600">
                              Link {idx + 1}:
                            </label>
                            <input
                              type="url"
                              value={link}
                              onChange={(e) => handleLinkChange(idx, e.target.value)}
                              placeholder="https://www.example.com/product-page"
                              className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-btn focus:ring-1 focus:ring-btn -ml-16"
                              disabled={verifying}
                            />
                            {link && (
                              <button
                                onClick={() => handleLinkChange(idx, "")}
                                className="text-gray-400 hover:text-red-500 text-lg w-6 h-6 flex items-center justify-center"
                                disabled={verifying}
                              >
                                &times;
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="px-6 py-3 border-b flex items-center gap-3">
                    <select
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value as ModelValue)}
                      className="rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-btn focus:ring-1 focus:ring-btn bg-white"
                      disabled={verifying}
                    >
                      {AI_MODELS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    <Button
                      text="Verify"
                      onButtonClick={handleVerify}
                      btnClass="bg-btn px-[25px] py-[8px]"
                      disabled={!canVerify || verifying}
                      loading={verifying}
                      loadingText="Verifying..."
                    />
                    <Button
                      text="Reset"
                      onButtonClick={handleReset}
                      btnClass="bg-gray-500 hover:bg-gray-600 px-[25px] py-[8px]"
                      disabled={verifying}
                    />
                  </div>

                  {/* Results Area */}
                  <div className="px-6 py-4 max-h-[500px] overflow-y-auto">
                    {verifying && (
                      <div className="flex flex-col items-center justify-center py-12 gap-4">
                        <Loader size="w-10 h-10" />
                        <p className="text-gray-500 text-sm">{statusMsg}</p>
                        <p className="text-gray-400 text-xs">
                          AI is attempting to fetch all links and will proceed with any that load successfully...
                        </p>
                      </div>
                    )}

                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-700 font-medium">Verification Failed</p>
                        <p className="text-red-600 text-sm mt-1">{error}</p>
                      </div>
                    )}

                    {validationWarning && (
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4 mb-4">
                        <p className="text-yellow-800 font-medium text-sm whitespace-pre-line">{validationWarning}</p>
                      </div>
                    )}

                    {result && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-bold text-gray-700 mb-3">Verification Report</h4>
                        <div className="ai-verification-result prose prose-sm max-w-none text-gray-800">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{result}</ReactMarkdown>
                        </div>
                      </div>
                    )}

                    {!verifying && !result && !error && (
                      <div className="text-center py-8 text-gray-400">
                        <p className="text-lg">
                          Enter reference links and click "Verify" to start AI verification
                        </p>
                        <p className="text-sm mt-2">
                          The AI will fetch the URLs and compare specifications automatically
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-3 bg-gray-50 border-t flex justify-end">
                    <Button
                      text="Close"
                      onButtonClick={handleClose}
                      btnClass="bg-gray-500 hover:bg-gray-600 px-[20px] py-[8px]"
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
